import { afterNextRender, ChangeDetectorRef, Component, effect, inject, Injector, viewChild } from '@angular/core';
import { Address, AddressInput, Asset, JournalAttachedEntityType, TaskAttachedEntityType } from '../../../../generated/generated-types';
import { WidgetAssetsManagerComponent } from '../../../asset/widget-assets-manager/widget-assets-manager.component';

import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AssetFileInputComponent } from '../../../../shared/components/asset-file-input/asset-file-input.component';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { WidgetAssetListComponent } from '../../../../../app/modules/products/components/widget-asset-list/widget-asset-list.component';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { NotificationService } from '../../../../@core/services/notification/notification.service';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { AssetService } from '../../../asset/services/asset.service';
import { JournalListWidgetComponent } from '../../../journal/components/journal-list-widget/journal-list-widget.component';
import { TaskListWidgetComponent } from '../../../task/components/task-list-widget/task-list-widget.component';
import { AddressService } from '../../services/address.service';

@Component({
  selector: 'app-widget-address-assets-manager',
  templateUrl: './widget-address-assets-manager.component.html',
  standalone: true,
  imports: [
    WidgetAssetListComponent,
    AssetFileInputComponent,
    BlockableDiv,
    TranslatePipe,
    ButtonModule,
    TooltipModule,
    JournalListWidgetComponent,
    TaskListWidgetComponent,
  ]
})
export class WidgetAddressAssetsManagerComponent extends WidgetAssetsManagerComponent<Address> {

  private readonly injector = inject(Injector);

  assetListRef = viewChild<WidgetAssetListComponent>('assetList');
  private readonly journalListWidget = viewChild<JournalListWidgetComponent>('journalListWidget');
  private readonly taskListWidget = viewChild<TaskListWidgetComponent>('taskListWidget');

  isSaving: boolean = false;

  /** Last entity id we applied journal/task filters for — prevents refresh loops. */
  private attachedFiltersAppliedForId: string | null = null;

  get address() {
    return this.model as Address;
  }

  get isBlocked(): boolean {
    return !this.model?.id;
  }

  /** Viewport fill only while blocked — never tab fillHeight (that kills page scroll). */
  get blockableClass(): string {
    return this.isBlocked
      ? 'flex min-h-0 w-full flex-col'
      : 'w-full';
  }

  get blockedStyle(): Record<string, string> {
    return this.isBlocked
      ? { height: 'calc(100vh - 14rem)', minHeight: 'calc(100vh - 14rem)' }
      : {};
  }

  constructor(
    public addressService: AddressService,
    public assetService: AssetService,
    public notificationService: NotificationService,
    public i18n: I18nService,
    private cdRef: ChangeDetectorRef
  ) {
    super(addressService, assetService, notificationService, i18n);

    this.setSelectedItemAsFeature = this.setSelectedItemAsFeature.bind(this);

    this.assetsAdded.subscribe(x => {
      this.model.assets = this.model.assets.concat(x);
    });

    // Apply once when list hosts appear — guarded so refresh cannot loop.
    effect(() => {
      const journal = this.journalListWidget();
      const task = this.taskListWidget();
      if (!journal && !task) {
        return;
      }
      this.applyAttachedEntityFilters();
    });

    afterNextRender(() => {
      this.applyAttachedEntityFilters();
    }, { injector: this.injector });
  }

  async setSelectedItemAsFeature() {
    const selectedItems: Asset[] = this.assetListRef().selectedItems();

    if (selectedItems.length > 0) {
      const updateAddress: AddressInput = {
        id: this.model.id,
        featuredAssetId: selectedItems[0].id
      };

      await this.addressService.saveItem(updateAddress);
      this.notificationService.success(_("common.save-successful"));
    }
  }

  checkIfCanSave() {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Address>;
    return component.detailForm.invalid;
  }

  async saveToUnlockEditMode() {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Address>;
    if (!component) {
      console.error("No detail component found to save Asset");
      return;
    }

    this.isSaving = true;
    this.cdRef.detectChanges();

    await component.save();

    this.isSaving = false;
    this.attachedFiltersAppliedForId = null;
    this.cdRef.detectChanges();

    afterNextRender(() => {
      this.applyAttachedEntityFilters();
    }, { injector: this.injector });
  }

  private applyAttachedEntityFilters(): void {
    const addressId = this.address?.id;
    if (!addressId) {
      return;
    }

    const journal = this.journalListWidget();
    const task = this.taskListWidget();
    if (!journal && !task) {
      return;
    }

    if (this.attachedFiltersAppliedForId === addressId) {
      return;
    }

    journal?.setCustomSearchInput({
      attachedEntityType: JournalAttachedEntityType.ADDRESS,
      attachedEntityId: addressId,
    });

    task?.setCustomSearchInput({
      attachedEntityType: TaskAttachedEntityType.ADDRESS,
      attachedEntityId: addressId,
    });

    this.attachedFiltersAppliedForId = addressId;
  }

}
